import { supabase } from '../../lib/supabase';

/** Edge function that lists, previews and sends marketing campaigns (platform admins only). */
export const MARKETING_FUNCTION = 'marketing-campaigns';

/** Error from the marketing-campaigns function. `status` 0 means the request never got a reply. */
export class MarketingError extends Error {
  /**
   * @param {string} message - Message.
   * @param {number} status - HTTP status (0 when the request never got a reply).
   * @param {object|null} [body=null] - Parsed reply body, when there was one.
   */
  constructor(message, status, body = null) {
    super(message);
    this.name = 'MarketingError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Current access token, or null.
 * @returns {Promise<string|null>} Token.
 */
async function accessToken() {
  try {
    const result = await supabase?.auth?.getSession?.();
    return result?.data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Call one marketing-campaigns action with the signed-in user's session token.
 * @param {'list'|'preview'|'test_send'|'send'|'history'|'suppress'} action - Action.
 * @param {object} [params] - Extra body fields (e.g. { campaignKey }).
 * @param {{signal?: AbortSignal}} [options] - Fetch options.
 * @returns {Promise<object>} Parsed JSON reply.
 * @throws {MarketingError} On any non-2xx reply or network failure.
 */
export async function callMarketing(action, params = {}, options = {}) {
  if (typeof fetch !== 'function') throw new MarketingError('Network unavailable.', 0);
  const token = await accessToken();
  if (!token) throw new MarketingError('Not signed in.', 401);
  const base = process.env.REACT_APP_SUPABASE_URL || '';
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  let response;
  try {
    response = await fetch(`${base}/functions/v1/${MARKETING_FUNCTION}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(anonKey ? { apikey: anonKey } : {}),
      },
      body: JSON.stringify({ ...params, action }),
      signal: options.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new MarketingError('Could not reach the campaigns service. Check your connection and try again.', 0);
  }
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const message = (body && (body.error || body.message)) || `Request failed (${response.status}).`;
    throw new MarketingError(String(message), response.status, body);
  }
  return body || {};
}

/**
 * Campaign cards plus sending config.
 * @param {{signal?: AbortSignal}} [options] - Fetch options.
 * @returns {Promise<{campaigns?: object[], config?: object}>} Reply.
 */
export const listCampaigns = (options) => callMarketing('list', {}, options);

/**
 * Rendered email for the calling admin.
 * @param {string} campaignKey - Campaign key.
 * @returns {Promise<{subject: string, preheader: string, html: string, text: string}>} Reply.
 */
export const previewCampaign = (campaignKey) => callMarketing('preview', { campaignKey });

/**
 * Send one test email to the caller's own address.
 * @param {string} campaignKey - Campaign key.
 * @returns {Promise<object>} Reply.
 */
export const testSendCampaign = (campaignKey) => callMarketing('test_send', { campaignKey });

/**
 * Send a campaign to its current audience. The server refuses (409) unless confirmCount equals
 * the current eligible count.
 * @param {string} campaignKey - Campaign key.
 * @param {number} confirmCount - The exact number the admin confirmed.
 * @returns {Promise<{sent: number, failed: number, remainingEligible: number, cappedByDailyLimit: boolean}>} Reply.
 */
export const sendCampaign = (campaignKey, confirmCount) => callMarketing('send', { campaignKey, confirmCount });

/**
 * Recent runs and sends, newest first.
 * @param {{campaignKey?: string, limit?: number}} [params] - Filters.
 * @param {{signal?: AbortSignal}} [options] - Fetch options.
 * @returns {Promise<{runs?: object[], sends?: object[]}>} Reply.
 */
export const campaignHistory = (params = {}, options) => callMarketing('history', params, options);

/**
 * Suppress one address by hand (reason 'manual'), for example an unsubscribe request that came
 * in by email. An existing suppression is kept as it is.
 * @param {string} email - Address.
 * @returns {Promise<{ok: boolean, email: string, reason: string, changed: boolean}>} Reply.
 */
export const suppressAddress = (email) => callMarketing('suppress', { email });
